import { z } from "zod";
import { B58_RE, base58ByteLength } from "./b58.ts";

export const EVMTokenAddress = z
    .string()
    .regex(/^0x[a-fA-F0-9]*$/, {
        message: "Must be a hex string beginning with 0x",
    })
    .transform((a) => a.toLowerCase() as `0x${string}`);
export type EVMTokenAddress = z.infer<typeof EVMTokenAddress>;

export const EVMAddress = z
    .string()
    .regex(/^0x[a-fA-F0-9]{40}$/, {
        message: "Must be a 40-character hex string beginning with 0x",
    })
    .transform((a) => a.toLowerCase() as `0x${string}`);
export type EVMAddress = z.infer<typeof EVMAddress>;

export const SolAddress = z.string().superRefine((s, ctx) => {
    if (!B58_RE.test(s)) {
        return ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Must be a base58-encoded Solana address" });
    }
    if (base58ByteLength(s) !== 32) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Solana address must decode to exactly 32 bytes" });
    }
});
export type SolAddress = z.infer<typeof SolAddress>;

export const BtcAddress = z.string().regex(
    /^(1[1-9A-HJ-NP-Za-km-z]{25,34}|3[1-9A-HJ-NP-Za-km-z]{25,34}|[bB][cC]1[a-z0-9]{25,87}|[bB][cC]1[A-Z0-9]{25,87})$/,
    { message: "Must be a valid Bitcoin address (P2PKH, P2SH, or Bech32/Bech32m)" },
).transform((a) => /^[bB][cC]1/.test(a) ? a.toLowerCase() : a);
export type BtcAddress = z.infer<typeof BtcAddress>;

export const TonAddress = z.string().regex(
    /^[a-zA-Z0-9_-]{48}$/,
    { message: "Must be a valid TON user-friendly address (48-character base64url)" },
);
export type TonAddress = z.infer<typeof TonAddress>;

export const TronAddress = z.string().superRefine((s, ctx) => {
    if (!/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(s)) {
        return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be a base58check Tron address starting with T (34 characters)",
        });
    }
    if (base58ByteLength(s) !== 25) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Tron address must decode to exactly 25 bytes" });
    }
});
export type TronAddress = z.infer<typeof TronAddress>;

export const AleoAddress = z.string().regex(/^aleo1[a-z0-9]{58}$/, {
    message: "Must be a valid Aleo address starting with aleo1 and 58 characters",
});
export type AleoAddress = z.infer<typeof AleoAddress>;

export const DogeAddress = z.string().superRefine((s, ctx) => {
    if (!/^[DA9][1-9A-HJ-NP-Za-km-z]{25,33}$/.test(s)) {
        return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be a valid Dogecoin address (P2PKH starting with D, or P2SH starting with A or 9)",
        });
    }
    if (base58ByteLength(s) !== 25) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Dogecoin address must decode to exactly 25 bytes" });
    }
});
export type DogeAddress = z.infer<typeof DogeAddress>;

export const LtcAddress = z.string().regex(
    /^([LM3][1-9A-HJ-NP-Za-km-z]{25,33}|ltc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39,59})$/,
    {
        message:
            "Must be a valid Litecoin address (P2PKH starting with L, P2SH starting with M or 3, or Bech32 starting with ltc1)",
    },
);
export type LtcAddress = z.infer<typeof LtcAddress>;

export const XrplAddress = z.string().regex(
    /^(r[1-9A-HJ-NP-Za-km-z]{24,34}|X[1-9A-HJ-NP-Za-km-z]{46})$/,
    { message: "Must be a valid XRPL address (classic r-address or X-address)" },
);
export type XrplAddress = z.infer<typeof XrplAddress>;

export const SuiAddress = z
    .string()
    .regex(/^0x[0-9a-fA-F]{64}$/, {
        message: "Must be a 32-byte Sui address (0x followed by 64 hex characters)",
    })
    .transform((a) => a.toLowerCase() as `0x${string}`);
export type SuiAddress = z.infer<typeof SuiAddress>;

export const ZecAddress = z.string().superRefine((s, ctx) => {
    if (!/^t[13][1-9A-HJ-NP-Za-km-z]{33}$/.test(s)) {
        return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be a transparent Zcash address (P2PKH starting with t1, or P2SH starting with t3)",
        });
    }
    if (base58ByteLength(s) !== 26) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Zcash address must decode to exactly 26 bytes" });
    }
});
export type ZecAddress = z.infer<typeof ZecAddress>;

export const Address = z.string();
export type Address = z.infer<typeof Address>;

export const AddressFamilyMap = z.object(
    {
        EVM: EVMAddress,
        SOL: SolAddress,
        BTC: BtcAddress,
        TON: TonAddress,
        TRON: TronAddress,
        ALEO: AleoAddress,
        DOGE: DogeAddress,
        LTC: LtcAddress,
        XRPL: XrplAddress,
        SUI: SuiAddress,
        ZEC: ZecAddress,
    } satisfies Record<string, z.ZodType<Address>>,
);
export type AddressFamilyMap = z.infer<typeof AddressFamilyMap>;

export const AddressFamily = AddressFamilyMap.keyof();
export type AddressFamily = z.infer<typeof AddressFamily>;

export const Amount = z.string().regex(/^(\d+|\d*\.\d+)$/);
export type Amount = z.infer<typeof Amount>;

const ORIGIN_LABEL = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

export const OriginPattern = z.string().trim().toLowerCase().superRefine((pattern, ctx) => {
    const fail = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });

    if (pattern.length === 0) {
        return fail("Must be a bare host, optionally with a port");
    }
    if (pattern.includes("://")) {
        return fail("Must not include a scheme");
    }
    if (/[/?#@\s]/.test(pattern)) {
        return fail("Must not include a path, query, or credentials");
    }

    const port = pattern.match(/:(\d{1,5})$/);
    if (port) {
        const number = parseInt(port[1]);
        if (number === 80 || number === 443) {
            return fail("Browsers omit default ports from Origin, so :80 and :443 can never match");
        }
        if (number < 1 || number > 65535) {
            return fail("Port must be between 1 and 65535");
        }
    } else if (pattern.includes(":")) {
        return fail("Port must be numeric");
    }

    const host = port ? pattern.slice(0, -port[0].length) : pattern;
    const wildcard = host.startsWith("*.");
    const bare = wildcard ? host.slice(2) : host;
    if (bare.includes("*")) {
        return fail("Wildcard is only allowed as a leading *. subdomain prefix");
    }

    const labels = bare.split(".");
    if (labels.some((label) => !ORIGIN_LABEL.test(label))) {
        return fail("Host contains invalid characters");
    }
    if (wildcard && labels.length < 2) {
        return fail("Wildcard must be scoped under a domain the org controls");
    }
});
export type OriginPattern = z.infer<typeof OriginPattern>;

export const Percentage = Amount
    .regex(/^\d+(\.\d{1,2})?$/, "Percentage must be a number with at most 2 decimal places")
    .refine((val) => {
        const parsed = Number(val);
        return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
    }, { message: "Percentage must be between 0 and 100" });
export type Percentage = z.infer<typeof Percentage>;

export const Balance = z.bigint();
export type Balance = z.infer<typeof Balance>;

export const Bool = z.union([
    z.boolean(),
    z.literal(0),
    z.literal(1),
    z.literal("0"),
    z.literal("1"),
    z.literal("false"),
    z.literal("true"),
]).transform((v) => v === true || v === 1 || v === "1" || v === "true");
export type Bool = z.infer<typeof Bool>;

export const Sau = z.bigint().min(0n);
export type Sau = z.infer<typeof Sau>;

export const BlockNumber = z.bigint().min(0n);
export type BlockNumber = z.infer<typeof BlockNumber>;

export const Milliseconds = z.number();
export type Milliseconds = z.infer<typeof Milliseconds>;

export const None = z.object({});
export type None = z.infer<typeof None>;

export const TxId = z.string();
export type TxId = z.infer<typeof TxId>;

export const PositiveNumeric = z.preprocess(
    (val) => {
        if (typeof val === "string" || typeof val === "number") {
            return Number(val);
        }
        return val;
    },
    z.number().refine((val) => Number.isFinite(val) && Number.isInteger(val) && val > 0),
);
export type PositiveNumeric = z.infer<typeof PositiveNumeric>;

export const NonNegativeNumeric = z.preprocess(
    (val) => {
        if (typeof val === "string" || typeof val === "number") {
            return Number(val);
        }
        return val;
    },
    z.number().refine((val) => Number.isFinite(val) && Number.isInteger(val) && val >= 0),
);
export type NonNegativeNumeric = z.infer<typeof NonNegativeNumeric>;

export const Signature = z.string();
export type Signature = z.infer<typeof Signature>;

export const HttpsUrl = z.string().regex(/^https:\/\/.+$/);
export type HttpsUrl = z.infer<typeof HttpsUrl>;

export const SafeUrl = z
    .string()
    .min(1)
    .refine((url) => !/^(javascript|data|blob|vbscript):/i.test(url), {
        message: "Blocked URL scheme",
    })
    .refine((url) => /^[a-zA-Z][a-zA-Z0-9+\-.]*:\/?\/?/.test(url), {
        message: "Must be a valid URL with a scheme (e.g. https://, myapp://)",
    });
export type SafeUrl = z.infer<typeof SafeUrl>;

export const StateAlpha2 = z.string().length(2);
export type StateAlpha2 = z.infer<typeof StateAlpha2>;

export const CountryAlpha2 = z.string().length(2);
export type CountryAlpha2 = z.infer<typeof CountryAlpha2>;

export const CountryAlpha3 = z.string().length(3);
export type CountryAlpha3 = z.infer<typeof CountryAlpha3>;

export const Timezone = z.string().refine((tz) => {
    if (/^[+-]/.test(tz)) {
        return false;
    }
    try {
        new Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
    } catch {
        return false;
    }
});
export type Timezone = z.infer<typeof Timezone>;

export const Ip = z.union([z.ipv4(), z.ipv6()]);
export type Ip = z.infer<typeof Ip>;

export const Geolocation = z.object({
    ip_address: Ip,
    alpha3_country_code: CountryAlpha3,
    state_code: z.union([StateAlpha2, z.literal("")]),
});
export type Geolocation = z.infer<typeof Geolocation>;

export const IChain = z.union([
    z.string(),
    z.object({
        network: z.string(),
    }),
]).transform((chain) => typeof chain === "string" ? chain : chain.network);
export type IChain = z.infer<typeof IChain>;

export const IToken = z.string();
export type IToken = z.infer<typeof IToken>;

export const IFiat = z.string();
export type IFiat = z.infer<typeof IFiat>;

export const IAsset = z.union([IFiat, IToken]);
export type IAsset = z.infer<typeof IAsset>;

export const ChainKey = z.string();
export type ChainKey = z.infer<typeof ChainKey>;

export const FiatKey = z.string();
export type FiatKey = z.infer<typeof FiatKey>;

export const TokenKey = z.string();
export type TokenKey = z.infer<typeof TokenKey>;

export const PayinMethodKey = z.string();
export type PayinMethodKey = z.infer<typeof PayinMethodKey>;

export const Price = Amount;
export type Price = z.infer<typeof Price>;

export const IPrices = z.record(IAsset, Price);
export type IPrices = z.infer<typeof IPrices>;

export const IAssetAmount = z.object({
    asset: IAsset,
    amount: Amount,
});
export type IAssetAmount = z.infer<typeof IAssetAmount>;

export const ITokenAmount = z.object({
    token: IToken,
    amount: Amount,
});
export type ITokenAmount = z.infer<typeof ITokenAmount>;

export const Limits = z.object({
    min: Amount.optional(),
    max: Amount.optional(),
});
export type Limits = z.infer<typeof Limits>;

export const PayinMethod = z.enum([
    "CREDIT_CARD",
    "DEBIT_CARD",
    "ACH",
    "FIAT_BALANCE",
    "TOKEN_BALANCE",
    "APPLE_PAY",
    "PAYPAL",
    "VENMO",
    "REVOLUT",
    "GOOGLE_PAY",
    "SEPA",
    "FASTER_PAYMENTS",
    "PIX",
    "SPEI",
    "UPI",
    "WIRE",
    "SWIFT",
]);
export type PayinMethod = z.infer<typeof PayinMethod>;

export const PayoutMethod = z.string();
export type PayoutMethod = z.infer<typeof PayoutMethod>;

export const IAmountIssue = z.object({
    kind: z.union([z.literal("amount"), z.literal("amount-downstream")]),
    asset: IAsset,
    given: Amount,
    limits: Limits,
    downstream_limits: Limits.optional(),
    source: z.string(),
    message: z.string(),
    reason: z.enum(["TOO_LOW", "TOO_HIGH", "UNEXPECTEDLY_LOW", "UNEXPECTEDLY_HIGH", "NO_VALID_AMOUNT", "UNKNOWN"]),
});
export type IAmountIssue = z.infer<typeof IAmountIssue>;

export const ParkedFundClassification = z.enum([
    "MISSENT",
    "STUCK",
    "UNDERFUNDED",
    "STALLED",
    "EXTRA",
    "SLIPPED",
    "DELAYED",
]);
export type ParkedFundClassification = z.infer<typeof ParkedFundClassification>;

export const ParkedFundSeverity = z.enum(["HIGH", "LOW"]);
export type ParkedFundSeverity = z.infer<typeof ParkedFundSeverity>;

export const ParkedFundSeverities = {
    MISSENT: "HIGH",
    STUCK: "HIGH",
    UNDERFUNDED: "HIGH",
    STALLED: "LOW",
    EXTRA: "HIGH",
    SLIPPED: "HIGH",
    DELAYED: "LOW",
} as const satisfies Record<ParkedFundClassification, ParkedFundSeverity>;

// XXX: can retire once `parked_fund` (below) is fully adopted.
export const IFundingIssue = z.object({
    kind: z.literal("funding"),
    token: IToken,
    balance: Sau,
});
export type IFundingIssue = z.infer<typeof IFundingIssue>;

export const WithdrawAccount = z.enum(["INTENT", "SPW", "RDW"]);
export type WithdrawAccount = z.infer<typeof WithdrawAccount>;

export const IPaymentGist = z.object({
    op: z.literal("buy"),
    destination_address: Address,
    destination_token: IToken,
});
export type IPaymentGist = z.infer<typeof IPaymentGist>;

export const IParkedFundWithdrawSource = z.discriminatedUnion("withdraw_type", [
    z.object({
        withdraw_type: z.literal("PAYMENT"),
        payment_id: z.uuid(),
        withdraw_account: WithdrawAccount,
    }),
    z.object({
        withdraw_type: z.literal("EXTRA_RDW_BALANCE"),
        rdw_address: Address,
    }),
]);
export type IParkedFundWithdrawSource = z.infer<typeof IParkedFundWithdrawSource>;

export const IParkedFundIssue = z.object({
    kind: z.literal("parked_fund"),
    token: IToken,
    balance: Sau,
    classification: ParkedFundClassification,
    severity: ParkedFundSeverity,
    payment_gist: IPaymentGist.nullable(),
    // null marks funds with no withdraw route, such as a deposit still in flight.
    withdraw_from: IParkedFundWithdrawSource.nullable(),
});
export type IParkedFundIssue = z.infer<typeof IParkedFundIssue>;

export const OwnerIssue = z.object({
    kind: z.literal("owner"),
    message: z.string(),
    mitigation: z.union([z.literal("change"), z.literal("verify")]),
});
export type OwnerIssue = z.infer<typeof OwnerIssue>;

export const GeolocationIssue = z.object({ kind: z.literal("geolocation"), message: z.string() });
export type GeolocationIssue = z.infer<typeof GeolocationIssue>;

export const DestinationIssue = z.object({ kind: z.literal("destination"), message: z.string() });
export type DestinationIssue = z.infer<typeof DestinationIssue>;

export const OtherIssue = z.object({ kind: z.literal("other"), message: z.string() });
export type OtherIssue = z.infer<typeof OtherIssue>;

export const PayinMethodIssue = z.object({ kind: z.literal("payin_method"), message: z.string() });
export type PayinMethodIssue = z.infer<typeof PayinMethodIssue>;

export const PayoutMethodIssue = z.object({ kind: z.literal("payout_method"), message: z.string() });
export type PayoutMethodIssue = z.infer<typeof PayoutMethodIssue>;

export const ProviderIssue = z.object({ kind: z.literal("provider"), message: z.string() });
export type ProviderIssue = z.infer<typeof ProviderIssue>;

export const UnknownIssue = z.object({ kind: z.literal("unknown"), message: z.string() });
export type UnknownIssue = z.infer<typeof UnknownIssue>;

export const IConfigurationIssue = z.object({
    kind: z.literal("org_configuration"),
    message: z.string(),
    rev_share_pct: z.string().optional(),
    total_pct: z.string().optional(),
    threshold: z.string().optional(),
});
export type IConfigurationIssue = z.infer<typeof IConfigurationIssue>;

export const KycIssue = z.object({
    kind: z.literal("kyc"),
    message: z.string(),
    provider_id: z.string().optional(),
});
export type KycIssue = z.infer<typeof KycIssue>;

export const IIssue = z.union([
    IAmountIssue,
    IFundingIssue,
    IParkedFundIssue,
    OwnerIssue,
    GeolocationIssue,
    DestinationIssue,
    OtherIssue,
    PayinMethodIssue,
    PayoutMethodIssue,
    ProviderIssue,
    UnknownIssue,
    IConfigurationIssue,
    KycIssue,
]);
export type IIssue = z.infer<typeof IIssue>;

export const IAssetDetails = z.union([
    z.object({
        issuer: z.string(),
        name: z.string(),
        symbol: z.string(),
        decimals: z.number(),
        alias: z.string().optional(),
        image_url: z.string().optional(),
    }),
    z.object({
        chain: IChain,
        address: Address,
        name: z.string(),
        symbol: z.string(),
        decimals: z.number(),
        alias: z.string().optional(),
        image_url: z.string().optional(),
        is_native: z.boolean().optional(),
        is_oft: z.boolean().optional(),
        coingecko_id: z.string().optional(),
        wrapper: z.string().optional(),
    }),
]);
export type IAssetDetails = z.infer<typeof IAssetDetails>;

export const IChainDetails = z.union([
    z.object({
        chain_id: z.bigint(),
        network: z.string(),
        display: z.string().optional(),
        address_family: AddressFamily,
        explorer: z.url().optional(),
        image: z.url().optional(),
        is_testnet: z.boolean(),
    }),
    z.object({
        chain_id: z.bigint(),
        network: z.string(),
        display: z.string().optional(),
        address_family: AddressFamily,
        explorer: z.url().optional(),
        image: z.url().optional(),
        is_testnet: z.boolean(),
        native_currency: z.object({
            name: z.string(),
            symbol: z.string(),
            decimals: z.number(),
        }),
        rpc: z.url().optional(),
    }),
]);
export type IChainDetails = z.infer<typeof IChainDetails>;

export const Account = z.enum([
    "USER",
    "DEST",
    "HALLIDAY",
    "SPW",
    "RDW",
    "REV_SHARE",
    "BRIDGE",
    "SPONSOR",
    "RAMP_DEPOSIT",
]);
export type Account = z.infer<typeof Account>;

export const Property = z.enum(["APPROVAL", "BALANCE", "SIDE_EFFECT"]);
export type Property = z.infer<typeof Property>;

export const IResource = z.object({
    asset: IAsset,
    property: Property,
});
export type IResource = z.infer<typeof IResource>;

export const IChangeStructure = z.object({
    account: Account,
    resource: IResource,
});
export type IChangeStructure = z.infer<typeof IChangeStructure>;

export const IEffectStructure = z.object({
    consume: z.array(IChangeStructure),
    produce: z.array(IChangeStructure),
});
export type IEffectStructure = z.infer<typeof IEffectStructure>;

export const IChange = IChangeStructure.extend({
    sau: Sau,
});
export type IChange = z.infer<typeof IChange>;

export const IEffect = z.object({
    consume: z.array(IChange),
    produce: z.array(IChange),
});
export type IEffect = z.infer<typeof IEffect>;

export const IPiece = z.object({
    type: z.string(),
});
export type IPiece = z.infer<typeof IPiece>;

export const PieceInfo = z.object({
    type: z.string(),
    custom_actions: z.any().optional(),
});
export type PieceInfo = z.infer<typeof PieceInfo>;

export const WalletPurpose = z.union([
    z.literal("QUOTE_SIGNER"),
    z.literal("AUCTION_AUTHORITY"),
    z.literal("TEST_FUNDER"),
    z.literal("SOLVER"),
]);
export type WalletPurpose = z.infer<typeof WalletPurpose>;

export const RevShareAddressMap = AddressFamilyMap.partial();
export type RevShareAddressMap = z.infer<typeof RevShareAddressMap>;
